import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import GateReturnDeclarations from './GateReturnDeclarations';
import { askReason } from '../utils/reportError';

// Gate Pass (mig 143). Role-aware:
//   store/stores_incharge: create/edit draft, submit, book returns, view own + Pending Returns
//   management/admin:       approve/reject/cancel + view all
//   gate:                   exit queue only — one action, Mark Exited
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const GP_ST = {
  draft: { c: '#888', l: 'Draft' }, pending_approval: { c: '#B45309', l: 'Pending Approval' },
  approved: { c: '#166534', l: 'Approved' }, exited: { c: '#3B3A8F', l: 'Exited' },
  partially_returned: { c: '#9A6B00', l: 'Partially Returned' }, closed: { c: '#555', l: 'Closed' },
  cancelled: { c: '#A32D2D', l: 'Cancelled' },
};
const badge = (s) => { const t = GP_ST[s] || { c: '#555', l: s }; return <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: t.c, background: '#f4f4f8' }}>{t.l}</span>; };
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };
const btnP = { padding: '8px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const btnG = { padding: '6px 12px', background: 'transparent', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' };

export default function GatePass({ user }) {
  const role = user?.role;
  const isGate = role === 'gate';
  const canCreate = ['store', 'stores_incharge', 'admin', 'management'].includes(role);
  const canApprove = ['admin', 'management'].includes(role);
  const canExit = ['gate', 'admin', 'management'].includes(role);
  // The GATE books returns too (mig 183): the guard's register records goods coming back in and
  // the operator keys that entry as gate@. The store keeps its access — 'gate' is added, not swapped.
  const canReturn = ['store', 'stores_incharge', 'admin', 'management', 'gate'].includes(role);

  const [rows, setRows] = useState([]);
  const [overdue, setOverdue] = useState([]);
  // A scoped list says what it withholds (item 42): passes with no company, or a login with none.
  const [withheldNote, setWithheldNote] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const [modal, setModal] = useState(null);       // create/edit form or null
  const [retModal, setRetModal] = useState(null);  // return receipt modal
  const [busy, setBusy] = useState('');
  // mig 353 — management's queue of returns raised without a register entry.
  const [view, setView] = useState('passes');   // passes | declarations
  const canSeeDeclarations = ['admin', 'management'].includes(role);

  const load = useCallback(() => {
    api.get('/gate-passes' + (isGate ? '?scope=gate' : ''))
      .then(r => { setRows(r.data.data || []); setWithheldNote(r.data.withheld?.note || ''); setLoadErr(''); })
      .catch(e => { setRows([]); setLoadErr(e.response?.data?.error || (e.response ? `the server answered ${e.response.status}` : 'no answer from the server')); });
    if (!isGate) api.get('/gate-passes/pending-returns').then(r => setOverdue(r.data.data || [])).catch(() => {});
  }, [isGate]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body) => {
    setBusy(id + path);
    try { await api.post(`/gate-passes/${id}/${path}`, body || {}); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); } finally { setBusy(''); }
  };
  const approve = (id) => act(id, 'approve');
  const reject = (id) => { const reason = window.prompt('Reason for rejection (optional):', ''); if (reason === null) return; act(id, 'reject', { reason }); };
  const cancel = (id) => { if (window.confirm('Cancel this gate pass?')) act(id, 'cancel'); };
  const submitPass = (id) => act(id, 'submit');
  // Exit now REQUIRES the gate register's out-entry number — the guard has already written it on
  // paper; gp_no is the store's number and is not the same thing. Prompt rather than confirm.
  const markExit = (id, gpNo) => {
    const no = window.prompt(`Gate OUT-entry number for ${gpNo}\n(from the gate register — required):`, '');
    if (no === null) return;                       // cancelled
    const v = String(no).trim();
    if (!v) { alert('Gate out-entry number is required to mark this pass exited.'); return; }
    act(id, 'exit', { gate_out_no: v });
  };
  const printPass = async (id) => {
    try { const r = await api.get(`/gate-passes/${id}/pdf`, { responseType: 'blob' }); const u = URL.createObjectURL(r.data); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch (e) { alert(e.response?.data?.error || 'Could not generate PDF'); }
  };
  const openEdit = async (id) => { try { const r = await api.get(`/gate-passes/${id}`); setModal({ ...r.data.data, _edit: true }); } catch { alert('Could not load'); } };
  const openReturn = async (p) => {
    try {
      const [d, h] = await Promise.all([
        api.get(`/gate-passes/${p.id}`),
        api.get(`/gate-passes/${p.id}/returns`).catch(() => ({ data: { data: [] } })),
      ]);
      const path = await api.get('/gate-passes/return-path').catch(() => ({ data: { data: null } }));
      setRetModal({ ...d.data.data, _q: Object.fromEntries((d.data.data.lines || []).map(l => [l.id, ''])),
                    note: '', gate_entry_no: '', evidence: 'gate_verified', _history: h.data.data || [], _path: path.data.data });
    } catch { alert('Could not load'); }
  };

  const rowActions = (p) => {
    const printable = ['approved', 'exited', 'partially_returned', 'closed'].includes(p.status);
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {canCreate && p.status === 'draft' && <button onClick={() => openEdit(p.id)} style={btnG}>Edit</button>}
        {canCreate && p.status === 'draft' && <button disabled={busy === p.id + 'submit'} onClick={() => submitPass(p.id)} style={{ ...btnG, color: '#3B3A8F', borderColor: '#3B3A8F' }}>Submit →</button>}
        {canApprove && p.status === 'pending_approval' && <>
          <button disabled={busy === p.id + 'approve'} onClick={() => approve(p.id)} style={{ ...btnG, color: '#166534', borderColor: '#166534', fontWeight: 600 }}>✓ Approve</button>
          <button onClick={() => reject(p.id)} style={{ ...btnG, color: '#A32D2D', borderColor: '#f0b4b4' }}>✕ Reject</button>
        </>}
        {canApprove && ['draft', 'pending_approval', 'approved'].includes(p.status) && <button onClick={() => cancel(p.id)} style={{ ...btnG, color: '#A32D2D' }}>Cancel</button>}
        {canExit && p.status === 'approved' && <button disabled={busy === p.id + 'exit'} onClick={() => markExit(p.id, p.gp_no)} style={{ ...btnP, padding: '6px 14px', fontSize: 12, background: '#3B3A8F' }}>🚪 Mark Exited</button>}
        {canReturn && p.type === 'returnable' && ['exited', 'partially_returned'].includes(p.status) && <button onClick={() => openReturn(p)} style={{ ...btnG, color: '#9A6B00', borderColor: '#f6d195', fontWeight: 600 }}>↩ Book Return</button>}
        {printable && <button onClick={() => printPass(p.id)} style={{ ...btnG, color: '#3B3A8F' }}>🖨 Print</button>}
      </div>
    );
  };

  return (
    <>
      <div style={{ marginBottom: 16, padding: '9px 13px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{isGate ? 'Gate security — approved passes awaiting exit, and returnable passes still out. Record the gate register number on both.'
          : 'Material out-passes. Store raises → management approves → gate marks exited → returnable items are returned.'}</span>
        <span style={{ display: 'flex', gap: 8 }}>
          {canSeeDeclarations && <button onClick={() => setView(v => v === 'passes' ? 'declarations' : 'passes')} style={btnG}>
            {view === 'passes' ? `🛡 Return declarations${rows.some(r => num(r.pending_declarations) > 0) ? ` (${rows.reduce((a, r) => a + num(r.pending_declarations), 0)})` : ''}` : '← Gate passes'}</button>}
          {canCreate && view === 'passes' && <button onClick={() => setModal({ type: 'returnable', lines: [{ item_desc: '', qty: '', uom: '' }] })} style={btnP}>+ New Gate Pass</button>}
        </span>
      </div>
      {view === 'declarations' && <GateReturnDeclarations />}
      {view === 'passes' && <>

      {/* Pending Returns (overdue) — store + management */}
      {withheldNote && (
        <div role="status" style={{ background: '#FFF4E5', border: '1px solid #F0C27A', color: '#7A4B00', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          {withheldNote}
        </div>
      )}
      {!isGate && overdue.length > 0 && (
        <div style={{ ...card, border: '1px solid #f6d195' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9A6B00', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>⚠️ Pending Returns — Overdue ({overdue.length})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['GP No', 'Party', 'Expected Return', 'Overdue', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {overdue.map(p => (
                <tr key={p.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{p.gp_no}</td>
                  <td style={td}>{p.party_destination || '—'}</td>
                  <td style={td}>{fmtD(p.expected_return_date)}</td>
                  <td style={{ ...td, color: '#A32D2D', fontWeight: 700 }}>{num(p.days_overdue)}d</td>
                  <td style={{ ...td, textAlign: 'right' }}>{canReturn && <button onClick={() => openReturn(p)} style={{ ...btnG, color: '#9A6B00', borderColor: '#f6d195', fontWeight: 600 }}>↩ Book Return</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{isGate ? 'Exit Queue' : 'Gate Passes'} ({rows.length})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['GP No', 'Gate Out No', 'Type', 'Party / Destination', 'Vehicle', 'Items', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {loadErr ? <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#A32D2D' }}>Could not load the gate passes ({loadErr}). This is not an empty list.</td></tr>
              : rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{isGate ? 'Nothing at the gate right now.' : 'No gate passes yet.'}</td></tr>}
            {rows.map(p => (
              <tr key={p.id}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{p.gp_no}</td>
                <td style={td}><GateOutNo p={p} /></td>
                <td style={td}>{p.type === 'returnable' ? <span style={{ color: '#9A6B00', fontWeight: 600 }}>Returnable</span> : <span style={{ color: '#555' }}>Non-ret.</span>}</td>
                <td style={td}>{p.party_destination || '—'}</td>
                <td style={td}>{p.vehicle_no || '—'}</td>
                <td style={td}>{num(p.line_count)}</td>
                <td style={td}>{badge(p.status)}
                  {num(p.pending_declarations) > 0 && <div style={{ fontSize: 10, color: '#9A6B00', marginTop: 3 }}
                    title="Counts as returned once a management login other than the raiser confirms it, with a reason.">
                    {num(p.pending_declarations)} return awaiting a second person's confirmation</div>}</td>
                <td style={{ ...td, textAlign: 'right' }}>{rowActions(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </>}
      {modal && <PassForm modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {retModal && <ReturnModal p={retModal} setP={setRetModal} user={user} onClose={() => setRetModal(null)} onSaved={() => { setRetModal(null); load(); }} />}
    </>
  );
}

// ── Gate OUT-entry number cell ──
// Three distinct states, and they must not look alike:
//   • has a number        → show it
//   • not exited yet      → "—", nothing has left the gate
//   • EXITED with no number→ the pass left before mig 183 existed. Shown as an explicit
//     "not recorded" rather than a blank, because a blank here reads as "the operator forgot"
//     and invites someone to invent a number after the fact. GP-2600006 and GP-2600007 are the
//     only two rows in this state and their register entries exist only on paper.
const GateOutNo = ({ p }) => {
  if (p.gate_out_no) return <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.gate_out_no}</span>;
  const exited = ['exited', 'partially_returned', 'closed'].includes(p.status);
  if (!exited) return <span style={{ color: '#ccc' }}>—</span>;
  return (
    <span title="This pass was exited before gate entry numbers were captured. The gate register entry exists on paper only; it has not been invented here."
      style={{ fontSize: 11, color: '#9A6B00', fontStyle: 'italic' }}>not recorded</span>
  );
};

// ── Create / edit draft form ──
function PassForm({ modal, onClose, onSaved }) {
  const editing = !!modal._edit;
  const [f, setF] = useState({
    type: modal.type || 'returnable', party_destination: modal.party_destination || '', vehicle_no: modal.vehicle_no || '',
    carried_by: modal.carried_by || '', reason: modal.reason || '', linked_doc_type: modal.linked_doc_type || '',
    linked_doc_id: modal.linked_doc_id || '', expected_return_date: (modal.expected_return_date || '').slice(0, 10),
    lines: (modal.lines && modal.lines.length ? modal.lines.map(l => ({ item_desc: l.item_desc, qty: l.qty, uom: l.uom || '' })) : [{ item_desc: '', qty: '', uom: '' }]),
  });
  const [challans, setChallans] = useState([]);
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => { if (f.linked_doc_type === 'jobwork_challan' && !challans.length) api.get('/jobwork/challans?').then(r => setChallans(r.data.data || [])).catch(() => {}); }, [f.linked_doc_type]); // eslint-disable-line
  const setLine = (i, patch) => setF(p => ({ ...p, lines: p.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const addLine = () => setF(p => ({ ...p, lines: [...p.lines, { item_desc: '', qty: '', uom: '' }] }));
  const rmLine = (i) => setF(p => ({ ...p, lines: p.lines.length > 1 ? p.lines.filter((_, j) => j !== i) : p.lines }));

  const save = async (submitAfter) => {
    const lines = f.lines.filter(l => String(l.item_desc || '').trim());
    if (!lines.length) { setErr('Add at least one item line'); return; }
    setBusy(true); setErr('');
    try {
      const body = { ...f, lines };
      let id = modal.id;
      if (editing) await api.patch(`/gate-passes/${id}`, body);
      else { const r = await api.post('/gate-passes', body); id = r.data.data.id; }
      if (submitAfter && id) await api.post(`/gate-passes/${id}/submit`);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 720, maxWidth: '96vw', margin: 'auto' }}>
        <h3 style={{ margin: '0 0 14px' }}>{editing ? `Edit Gate Pass ${modal.gp_no || ''}` : 'New Gate Pass'}</h3>
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>Type *</label>
            <select style={inp} value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
              <option value="returnable">Returnable</option><option value="non_returnable">Non-Returnable</option>
            </select></div>
          <div><label style={lbl}>Vehicle No</label><input style={inp} value={f.vehicle_no} onChange={e => setF({ ...f, vehicle_no: e.target.value })} /></div>
          <div><label style={lbl}>Carried By</label><input style={inp} value={f.carried_by} onChange={e => setF({ ...f, carried_by: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label style={lbl}>Party / Destination</label><input style={inp} value={f.party_destination} onChange={e => setF({ ...f, party_destination: e.target.value })} /></div>
          {f.type === 'returnable' && <div><label style={lbl}>Expected Return <span style={{ color: '#aaa' }}>(default +15d)</span></label><input type="date" style={inp} value={f.expected_return_date} onChange={e => setF({ ...f, expected_return_date: e.target.value })} /></div>}
          <div><label style={lbl}>Linked Doc</label>
            <select style={inp} value={f.linked_doc_type} onChange={e => setF({ ...f, linked_doc_type: e.target.value, linked_doc_id: '' })}>
              <option value="">— none —</option><option value="jobwork_challan">Job-Work Challan</option><option value="invoice">Invoice</option><option value="indent">Indent</option><option value="other">Other</option>
            </select></div>
          {f.linked_doc_type === 'jobwork_challan'
            ? <div style={{ gridColumn: '2 / 4' }}><label style={lbl}>Challan</label><select style={inp} value={f.linked_doc_id} onChange={e => setF({ ...f, linked_doc_id: e.target.value })}><option value="">— pick a challan —</option>{challans.map(c => <option key={c.id} value={c.id}>{c.challan_no}{c.jwo_no ? ` · ${c.jwo_no}` : ''}</option>)}</select></div>
            : f.linked_doc_type && f.linked_doc_type !== '' ? <div style={{ gridColumn: '2 / 4' }}><label style={lbl}>Reference</label><input style={inp} value={f.linked_doc_id} onChange={e => setF({ ...f, linked_doc_id: e.target.value })} placeholder="optional reference id" /></div> : null}
          <div style={{ gridColumn: '1 / 4' }}><label style={lbl}>Reason</label><input style={inp} value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} /></div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, margin: '6px 0 8px' }}>Items *</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
          <thead><tr>{['Item', 'Qty', 'UOM', ''].map(h => <th key={h} style={{ ...th, padding: '5px 6px' }}>{h}</th>)}</tr></thead>
          <tbody>
            {f.lines.map((l, i) => (
              <tr key={i}>
                <td style={{ ...td, padding: '4px 6px' }}><input style={inp} value={l.item_desc} onChange={e => setLine(i, { item_desc: e.target.value })} placeholder="e.g. Fabric roll — navy" /></td>
                <td style={{ ...td, padding: '4px 6px', width: 100 }}><input type="number" step="0.001" style={inp} value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} /></td>
                <td style={{ ...td, padding: '4px 6px', width: 90 }}><input style={inp} value={l.uom} onChange={e => setLine(i, { uom: e.target.value })} placeholder="pcs / kg" /></td>
                <td style={{ ...td, padding: '4px 6px', width: 30 }}>{f.lines.length > 1 && <button onClick={() => rmLine(i)} style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16 }}>×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addLine} style={{ ...btnG, marginBottom: 14 }}>＋ Add line</button>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnG}>Cancel</button>
          <button disabled={busy} onClick={() => save(false)} style={{ ...btnP, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>{busy ? '…' : 'Save Draft'}</button>
          <button disabled={busy} onClick={() => save(true)} style={btnP}>{busy ? '…' : 'Save & Submit →'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Return modal (mig 353): every return names its evidence ──
// gate_verified — books now, against an IN entry the gate register ALREADY holds. Nothing creates one.
// declared      — no register entry. Raised here, counts once a DIFFERENT personal management login
//                 declares it with a reason. A login that cannot raise one is told why, in the
//                 database's own words — never shown a dead option.
const EVIDENCE_LABEL = { legacy: 'legacy (booked before 2 Oct)', gate_verified: 'gate verified', declared: 'declared' };
function ReturnModal({ p, setP, onClose, onSaved, user }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [ok, setOk] = useState('');
  const path = p._path || {};
  const declared = p.evidence === 'declared';
  const book = async () => {
    setErr(''); setOk('');
    const gateNo = String(p.gate_entry_no || '').trim();
    if (!declared && !gateNo) { setErr('Gate entry number is required — take it from the gate register. If there is no register entry, choose "No register entry — declared".'); return; }
    if (!path.can_raise_declared) { setErr(path.why_not || 'This login cannot raise a return.'); return; }
    const lines = (p.lines || []).map(l => ({ line_id: l.id, qty: num(p._q[l.id]) })).filter(l => l.qty > 0);
    if (!lines.length) { setErr('Enter at least one return quantity'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/gate-passes/${p.id}/return`, { evidence: p.evidence, gate_entry_no: declared ? null : gateNo, lines, note: p.note });
      setOk(`Raised. It counts as returned once one of ${(path.declarers || []).filter(n => n !== user?.name).join(', ') || 'the management logins'} confirms it — not you.`);
      setTimeout(onSaved, 2500);
      return r;
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const reverse = async (h) => {
    const why = askReason(`Reverse the return booked on ${h.returned_at ? fmtD(h.returned_at) : '—'} (${(h.lines || []).map(l => `${l.item_desc} ${num(l.qty)}`).join(', ')})?\n\nThe quantities go back to OUT and the pass reopens. Nothing is deleted — the reversal is recorded against your name.\n\nWhat was wrong with this booking? (required, in words)`);
    if (why == null) return;
    try { await api.post(`/gate-passes/returns/${h.id}/reversal`, { reason: why }); onSaved(); }
    catch (e) { setErr(e.response?.data?.error || 'Reversal failed'); }
  };
  // Whether THIS user may reverse a given return, and if not, why — mirrors the trigger's rule.
  const reverseBlock = (h) => {
    if (h.return_status !== 'booked' || h.reversed_at) return 'none';
    if (user?.role !== 'management') return 'A management login reverses a return.';
    if (!path.can_declare) return path.why_not || 'This login is not a person, so it cannot reverse a return.';
    const first = h.declared_by || h.returned_by;
    if (String(first) === String(user?.id)) return h.declared_by ? 'You declared this — a different management login reverses it.' : 'You booked this — a different management login reverses it.';
    return null;
  };
  const card = (on) => ({ flex: 1, padding: '9px 11px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
    border: on ? '2px solid #3B3A8F' : '1px solid #ddd', background: on ? '#EEEDF8' : '#fff' });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 660, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 4px' }}>Book Return — {p.gp_no}</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>Enter returned qty per line (partial allowed). Full return on every line closes the pass.</p>
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        {ok && <div style={{ background: '#EAF7EE', color: '#166534', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{ok}</div>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={card(!declared)} onClick={() => setP(x => ({ ...x, evidence: 'gate_verified' }))}>
            <strong>Gate register IN entry exists</strong><div style={{ color: '#777', marginTop: 2 }}>Names the guard's entry; a second person confirms.</div></div>
          <div style={card(declared)} onClick={() => setP(x => ({ ...x, evidence: 'declared' }))}>
            <strong>No register entry — declared</strong><div style={{ color: '#777', marginTop: 2 }}>A second person's confirmation is the only evidence.</div></div>
        </div>
        {!declared ? (
          <div style={{ marginBottom: 14, padding: 12, background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6 }}>
            <label style={{ ...lbl, color: '#9A6B00' }}>Gate entry number *</label>
            <input style={inp} autoFocus value={p.gate_entry_no || ''}
              onChange={e => setP(x => ({ ...x, gate_entry_no: e.target.value }))}
              placeholder="IN-entry number from the gate register" />
            <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 4 }}>
              From the security guard's register — not {p.gp_no}, which is the store's number. It must already be in the register; booking will not create it.
              A second person still confirms the return.
            </div>
            {!path.can_raise_declared && <div style={{ marginTop: 8, color: '#A32D2D', fontSize: 12 }}><strong>This login cannot raise a return.</strong> {path.why_not}</div>}
          </div>
        ) : (
          <div style={{ marginBottom: 14, padding: 12, background: path.can_raise_declared ? '#F4F5FA' : '#FCEBEB', border: '1px solid ' + (path.can_raise_declared ? '#d8dbef' : '#f0cccc'), borderRadius: 6, fontSize: 12, color: path.can_raise_declared ? '#3B3A8F' : '#A32D2D' }}>
            {path.can_raise_declared
              ? <>No register number. This return is <strong>raised now and counts once confirmed</strong> by {(path.declarers || []).filter(n => n !== user?.name).join(', ') || 'a management login'} — not you.</>
              : <><strong>This login cannot raise a return.</strong> {path.why_not}
                  {path.counter && <div style={{ marginTop: 6, color: '#7C4A03' }}>
                    Store logins that can raise one: {path.counter.store_logins_that_can_raise} of {path.counter.store_logins_active}
                    {path.counter.store_logins_refused ? ` (${path.counter.store_logins_refused})` : ''}. {path.counter.passes_out} returnable passes are out; until a store login is a person, a management login raises them and a different one declares.</div>}</>}
          </div>
        )}
        {(p._history || []).length > 0 && (
          <div style={{ marginBottom: 12, fontSize: 12, color: '#555' }}>
            <div style={{ fontWeight: 700, color: '#3B3A8F', marginBottom: 4 }}>Returns on this pass ({p._history.length})</div>
            {p._history.map(h => { const rb = reverseBlock(h); return (
              <div key={h.id} style={{ padding: '5px 0', borderBottom: '1px solid #f2f2f2', opacity: h.reversed_at || h.return_status === 'declaration_rejected' ? 0.6 : 1 }}>
                <span style={{ fontWeight: 600 }}>{EVIDENCE_LABEL[h.evidence_kind] || h.evidence_kind}</span>
                {h.gate_entry_no && <span style={{ fontFamily: 'monospace' }}> · {h.gate_entry_no}</span>}
                <span style={{ color: '#888' }}> · {fmtD(h.returned_at)} · {h.returned_by_name || '—'} · </span>
                {(h.lines || []).map(l => `${l.item_desc} ${num(l.qty)}${l.uom ? ' ' + l.uom : ''}`).join(', ')}
                {h.return_status === 'pending_declaration' && <div style={{ color: '#9A6B00' }}>Awaiting confirmation by a management login other than {h.returned_by_name}.</div>}
                {h.declared_by_name && <div style={{ color: '#166534' }}>Confirmed by {h.declared_by_name}: “{h.declaration_reason}”</div>}
                {h.confirm_basis && <div style={{ color: '#555', fontSize: 11 }}>Rests on: {h.confirm_basis === 'party_challan' ? `party challan ${h.basis_ref} of ${fmtD(h.basis_date)}`
                  : h.confirm_basis === 'gate_register' ? `gate register IN entry ${h.basis_entry_no}`
                  : h.confirm_basis === 'physical_count' ? `a physical count by ${h.basis_counted_by}`
                  : `the party's word only — ${h.basis_stated_by}`}</div>}
                {h.return_status === 'declaration_rejected' && <div style={{ color: '#A32D2D' }}>Refused by {h.rejected_by_name}: “{h.rejection_reason}”</div>}
                {h.reversed_at && <div style={{ color: '#A32D2D' }}>Reversed by {h.reversed_by_name} on {fmtD(h.reversed_at)}: “{h.reversal_reason}”</div>}
                {rb === null && <button onClick={() => reverse(h)} style={{ ...btnG, marginTop: 4, color: '#A32D2D', borderColor: '#f0cccc' }}>↺ Reverse</button>}
                {rb && rb !== 'none' && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{rb}</div>}
              </div>
            ); })}
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead><tr>{['Item', 'Issued', 'Returned', 'Return now'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {(p.lines || []).map(l => { const outstanding = num(l.qty) - num(l.qty_returned); return (
              <tr key={l.id}>
                <td style={td}>{l.item_desc}</td>
                <td style={td}>{num(l.qty)} {l.uom || ''}</td>
                <td style={td}>{num(l.qty_returned)}</td>
                <td style={{ ...td, width: 120 }}><input type="number" step="0.001" min="0" max={outstanding} style={inp} value={p._q[l.id]} onChange={e => setP(x => ({ ...x, _q: { ...x._q, [l.id]: e.target.value } }))} placeholder={`≤ ${+outstanding.toFixed(3)}`} /></td>
              </tr>
            ); })}
          </tbody>
        </table>
        <div style={{ marginBottom: 14 }}><label style={lbl}>Process-loss / return note</label><input style={inp} value={p.note} onChange={e => setP(x => ({ ...x, note: e.target.value }))} placeholder="e.g. 2 pcs lost in wash" /></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnG}>Close</button>
          <button disabled={busy || !path.can_raise_declared} onClick={book} style={{ ...btnP, opacity: !path.can_raise_declared ? 0.5 : 1 }}>
            {busy ? 'Raising…' : 'Raise for confirmation'}</button>
        </div>
      </div>
    </div>
  );
}
